/** Seleção do aluno na camada de texto → retângulos normalizados da página.
 *
 * O DOM entra por SEAM injetável (`lerRects`, `lerSelecao`, `medirPagina`),
 * igual ao `PdfLoader`: o jsdom não implementa `Range.getClientRects()` nem
 * `getBoundingClientRect`, então um teste que dependesse deles passaria com
 * retângulos zerados. A matemática vive em `utils/rects.ts` e é testada lá.
 */
import { useCallback, useEffect, useState } from 'react'

import {
  ancoraDoMenu,
  envolvente,
  fundirLinhas,
  normalizarRects,
  type CaixaLike,
} from '@/features/reader/utils/rects'
import type { Rect } from '@/types/api'

export interface SelecaoDeTexto {
  texto: string
  /** Retângulos por linha — é o que pinta a marca sem cobrir a margem. */
  rects: Rect[]
  /** Envolvente — é o `rect` que o contrato do backend exige. */
  envolvente: Rect
  /** Onde ancorar o menu de cores, em coordenadas da página. */
  ancora: { x: number; y: number }
}

interface Opcoes {
  chave?: number
  /** Elemento com a caixa EXATA da página (o `.pdf-page-wrapper`). */
  pagina: HTMLElement | null
  habilitado?: boolean
  lerSelecao?: () => Selection | null
  lerRects?: (range: Range) => CaixaLike[]
  medirPagina?: (el: HTMLElement) => CaixaLike
}

export function useTextSelection({
  chave,
  pagina,
  habilitado = true,
  lerSelecao = () => window.getSelection(),
  lerRects = (range) => Array.from(range.getClientRects()),
  medirPagina = (el) => el.getBoundingClientRect(),
}: Opcoes) {
  const [selecao, setSelecao] = useState<(SelecaoDeTexto & { chave: number | undefined }) | null>(null)

  const limpar = useCallback(() => setSelecao(null), [])

  useEffect(() => {
    if (!habilitado || !pagina) return

    const aoMudar = () => {
      const nativa = lerSelecao()
      if (!nativa || nativa.isCollapsed || nativa.rangeCount === 0) {
        setSelecao(null)
        return
      }

      const texto = nativa.toString().trim()
      if (!texto) {
        setSelecao(null)
        return
      }

      const range = nativa.getRangeAt(0)
      // Seleção que começou fora da página (no chrome do app) não vira destaque.
      if (!pagina.contains(range.commonAncestorContainer)) {
        setSelecao(null)
        return
      }

      const rects = fundirLinhas(normalizarRects(lerRects(range), medirPagina(pagina)))
      const caixa = envolvente(rects)
      const ancora = ancoraDoMenu(rects)
      if (!caixa || !ancora) {
        setSelecao(null)
        return
      }

      setSelecao({ texto, rects, envolvente: caixa, ancora, chave })
    }

    document.addEventListener('selectionchange', aoMudar)
    return () => document.removeEventListener('selectionchange', aoMudar)
  }, [habilitado, pagina, lerSelecao, lerRects, medirPagina, chave])

  return { selecao: selecao?.chave === chave ? selecao : null, limpar }
}
